import React, { useState } from "react"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { selectedPracticeConfigurationSelector } from "../../../store/selectors/selectedPracticeConfiguration.selector"
import type { IReviewPayerMappingsState } from "../../../types/payerMappings.types"
import { PayerMappingsManageComponent } from "./payerMappingsManage.component"
import { PayerMappingsModeComponent } from "./payerMappingsMode.component"
import { PayerMappingsUnavailableComponent } from "./payerMappingsUnavailable.component"
import { PayerMappingsReviewComponent } from "./table/payerMappingsReview.component"
import { PayerMappingsTableComponent } from "./table/payerMappingsTable.component"

export const PayerMappingsComponent: React.FunctionComponent = () => {
    const selectedPracticeConfiguration = useAppSelector(
        selectedPracticeConfigurationSelector,
    )

    const hasPermissions = useHasPermissions(
        [
            OneVerifyPermission.readPracticeConfigurations,
            OneVerifyPermission.managePracticeConfigurations,
        ],
        undefined,
        false,
    )

    const [externalPayerIdSearch, setExternalPayerIdSearch] = useState<
        string | undefined
    >()

    const [reviewState, setReviewState] =
        useState<IReviewPayerMappingsState | null>(null)

    const selectedPracticePayerMappingsMode =
        selectedPracticeConfiguration?.payerMappingsMode

    if (!hasPermissions) {
        return null
    }

    // undefined means not set yet; null means there was an error (handled by PayerMappingsUnavailableComponent)
    if (selectedPracticePayerMappingsMode === undefined) {
        return null
    }

    const isPayerServiceAvailable = selectedPracticePayerMappingsMode !== null

    return (
        <div
            data-produktly="payer-mappings-page"
            className="max-w-[840px] mx-auto relative pb-[24px]"
        >
            <PageHelmetElement
                title={reviewState ? "Review Payer Mappings" : "Payer Mappings"}
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {isPayerServiceAvailable ? (
                reviewState ? (
                    <PayerMappingsReviewComponent
                        state={reviewState}
                        onClose={() => {
                            setReviewState(null)
                        }}
                        onReuploadDone={data => {
                            setReviewState(data)
                        }}
                        onUpsertSuccess={() => {
                            setExternalPayerIdSearch(undefined)

                            setReviewState(null)
                        }}
                    />
                ) : (
                    <>
                        <PayerMappingsModeComponent />

                        <PayerMappingsManageComponent
                            externalPayerIdSearch={externalPayerIdSearch}
                            onExternalPayerIdSearchChange={
                                setExternalPayerIdSearch
                            }
                            onValidationDone={data => {
                                setReviewState(data)
                            }}
                        />

                        <PayerMappingsTableComponent
                            externalPayerIdSearch={externalPayerIdSearch}
                        />
                    </>
                )
            ) : (
                <PayerMappingsUnavailableComponent />
            )}
        </div>
    )
}
